/**
 * @name nationalParkRequests
 * @description Function used to make requests to the national park endpoints
 * @param requestor
 * @returns {object} nationalParkRequests
 */
export const nationalParkRequests = (requestor: any) => ({
  /**
   * @name createNationalPark
   * @description Function used to make request to create a national park
   * @param {object} data
   * @returns {Promise<any>} response
   */
  createNationalPark: async (data: any) => {
    const url = `/nationalParks/create`;
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.descriptionTitle) formData.append('descriptionTitle', data.descriptionTitle);
    formData.append('description', data.description);
    formData.append('location', data.location);
    formData.append('bestTimeToVisit', data.bestTimeToVisit);
    formData.append('country', data.country);
    if (data.tours && data.tours.length > 0) formData.append('tours', data.tours);
    if (data.file) formData.append('file', data.file);
    if (data.blocks) formData.append('blocks', JSON.stringify(data.blocks));
    const response = await requestor({
      method: 'POST',
      url,
      data: formData,
    });
    return response;
  },

  /**
   * @name editNationalPark
   * @description Function used to make request to edit a national park
   * @param {object} data
   * @param {string} id
   * @returns {Promise<any>} response
   */
  editNationalPark: async (data: any, id: string) => {
    const url = `/nationalParks/update/${id}`;
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.descriptionTitle) formData.append('descriptionTitle', data.descriptionTitle);
    formData.append('description', data.description);
    formData.append('location', data.location);
    formData.append('bestTimeToVisit', data.bestTimeToVisit);
    formData.append('country', data.country);
    if (data.tours && data.tours.length > 0) formData.append('tours', data.tours);
    if (data.file) formData.append('file', data.file);
    if (data.blocks) formData.append('blocks', JSON.stringify(data.blocks));
    const response = await requestor({
      method: 'PATCH',
      url,
      data: formData,
    });
    return response;
  },

  /**
   * @name deleteNationalPark
   * @description Function used to make request to delete a national park
   * @param {string} id
   * @returns {object} response
   */
  deleteNationalPark: async (id: string) => {
    const url = `/nationalParks/delete/${id}`;
    const response = await requestor({
      method: 'DELETE',
      url,
    });
    return response;
  },

  /**
   * @name get - handle all get requests for national parks
   */
  get: {
    /**
     * @name getAllNationalParks
     * @description Function used to get all national parks
     * @returns {object} response
     */
    getAllNationalParks: async () => {
      const url = `/nationalParks/getAllNationalParks`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },

    /**
     * @name getNationalParksByCountry
     * @description Function used to get national parks by country
     * @param {string} countryId
     * @returns {object} response
     */
    getNationalParksByCountry: async (countryId: string) => {
      const url = `/nationalParks/getByCountry/${countryId}`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },

    /**
     * @name getNationalParkById
     * @description Function used to get a national park by id
     * @param {string} id
     * @returns {object} response
     */
    getNationalParkById: async (id: string) => {
      const url = `/nationalParks/${id}`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },

    /**
     * @name getNationalParkBySlug
     * @description Function used to get a national park by slug
     * @param {string} slug
     * @returns {object} response
     */
    getNationalParkBySlug: async (slug: string) => {
      const url = `/nationalParks/getBySlug/${slug}`;
      const response = await requestor({
        method: 'GET',
        url,
      });
      return response;
    },
  },
});
